import { create } from "zustand"
import { persist } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"

import { clearProfileCache } from "@/http/auth/get-profile"
import {
  clearSessionCache,
  ensureFreshAccessToken,
  ensureSession,
  hasActiveSession,
} from "@/http/auth/session"
import { isTokenExpired, isTokenExpiring } from "@/http/jwt"
import { defineAbilityFor, type TUser } from "@/lib/auth"
import { logger } from "@/lib/logger"
import { secureStorage } from "@/lib/secure-storage"

export type TAuthState = {
  // Authentication state
  isAuthenticated: boolean
  isLoading: boolean
  isNearExpiry: boolean

  // User profile
  user?: TUser

  // Core auth actions
  setTokens: (accessToken: string, refreshToken?: string) => void
  clearAuth: () => void
  setProfile: (user: TUser) => void
  setProfileName: (name: string) => void
  removeProfile: () => void
  checkAuthStatus: () => boolean

  // Token management
  getTokens: () => { access: string | null; refresh: string | null }
  shouldRefresh: () => boolean
  getTokenExpiration: (tokenType: "access" | "refresh") => Date | null

  // Permissions
  can: ReturnType<typeof defineAbilityFor>["can"]
  cannot: ReturnType<typeof defineAbilityFor>["cannot"]

  // Internal state management
  initialize: () => void
  startTokenMonitoring: () => void
  stopTokenMonitoring: () => void
}

let tokenMonitorInterval: NodeJS.Timeout | null = null
let authCheckInterval: NodeJS.Timeout | null = null
let isInitialized = false

export const useAuth = create(
  persist(
    immer<TAuthState>((set, get) => {
      const getUser = () => get()?.user

      // Initialize authentication state
      function initialize() {
        if (isInitialized) {
          logger.debug("Authentication already initialized, skipping")
          return
        }

        logger.info("Initializing authentication state")
        isInitialized = true

        const hasSession = hasActiveSession()

        set((state) => {
          state.isAuthenticated = hasSession
          state.isLoading = false
        })

        if (hasSession) {
          startTokenMonitoring()
        }

        if (typeof window !== "undefined" && hasSession) {
          void ensureSession().then((stillActive) => {
            if (!stillActive) {
              clearAuth()
            }
          })
        }

        // Set up periodic auth status check only if not already running
        if (typeof window !== "undefined" && !authCheckInterval) {
          authCheckInterval = setInterval(() => {
            const tokens = secureStorage.getTokens()
            const accessExpired = isTokenExpired(tokens.access)
            const refreshExpired = isTokenExpired(tokens.refresh)
            const currentStatus = !accessExpired || !refreshExpired
            const storeStatus = get().isAuthenticated

            if (accessExpired && refreshExpired) {
              clearAuth()
              return
            }

            if (currentStatus !== storeStatus) {
              logger.info("Authentication status changed", {
                from: storeStatus,
                to: currentStatus,
              })

              set((state) => {
                state.isAuthenticated = currentStatus
                if (!currentStatus) {
                  state.user = undefined
                  state.isNearExpiry = false
                }
              })

              if (currentStatus) {
                startTokenMonitoring()
              } else {
                stopTokenMonitoring()
              }
            }
          }, 5000) // Check every 5 seconds
        }
      }

      // Start token expiration monitoring
      function startTokenMonitoring() {
        stopTokenMonitoring() // Clear any existing interval

        if (typeof window !== "undefined") {
          tokenMonitorInterval = setInterval(() => {
            const tokens = secureStorage.getTokens()
            const accessExpired = isTokenExpired(tokens.access)
            const refreshExpired = isTokenExpired(tokens.refresh)
            if (accessExpired && refreshExpired) {
              clearAuth()
              return
            }

            const needsRefresh = shouldRefresh()
            const currentNearExpiry = get().isNearExpiry

            if (needsRefresh !== currentNearExpiry) {
              set((state) => {
                state.isNearExpiry = needsRefresh
              })
            }

            if (needsRefresh) {
              ensureFreshAccessToken().catch((err) => {
                logger.debug("Background token refresh failed", err)
              })
            }

            // More precise expiration checking
            const accessExpiration = getTokenExpiration("access")
            if (accessExpiration) {
              const timeUntilExpiry = accessExpiration.getTime() - Date.now()
              const fiveMinutes = 5 * 60 * 1000

              if (timeUntilExpiry < fiveMinutes && !currentNearExpiry) {
                logger.warn("Token expiring soon", { timeUntilExpiry })
                set((state) => {
                  state.isNearExpiry = true
                })
              }
            }
          }, 60000) // Check every minute
        }
      }

      // Stop token monitoring
      function stopTokenMonitoring() {
        if (tokenMonitorInterval) {
          clearInterval(tokenMonitorInterval)
          tokenMonitorInterval = null
        }
        if (authCheckInterval) {
          clearInterval(authCheckInterval)
          authCheckInterval = null
        }
      }

      // Check current authentication status
      function checkAuthStatus() {
        const tokens = secureStorage.getTokens()
        const accessExpired = isTokenExpired(tokens.access)
        const refreshExpired = isTokenExpired(tokens.refresh)
        const hasSession = !accessExpired || !refreshExpired

        if (accessExpired && refreshExpired) {
          clearAuth()
          return false
        }

        set((state) => {
          state.isAuthenticated = hasSession
          state.isLoading = false
        })

        return hasSession
      }

      // Set tokens securely
      function setTokens(accessToken: string, refreshToken?: string) {
        logger.info("Setting authentication tokens")
        secureStorage.setTokens(accessToken, refreshToken)

        set((state) => {
          state.isAuthenticated = true
          state.isLoading = false
          state.isNearExpiry = false
        })

        // Clear caches to avoid stale preloads after login
        clearSessionCache()
        clearProfileCache()

        startTokenMonitoring()

        // Trigger auth change event for external listeners
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("auth-change"))
        }
      }

      // Clear all authentication data
      function clearAuth() {
        logger.info("Clearing authentication")
        secureStorage.clearTokens()

        set((state) => {
          state.isAuthenticated = false
          state.isNearExpiry = false
          state.user = undefined
        })

        // Clear caches to avoid stale preloads after logout
        clearSessionCache()
        clearProfileCache()

        stopTokenMonitoring()
        isInitialized = false // Reset initialization flag

        // Trigger auth change event for external listeners
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("auth-change"))
        }
      }

      // Get current tokens
      function getTokens() {
        return secureStorage.getTokens()
      }

      // Check if token refresh is needed
      function shouldRefresh() {
        const tokens = secureStorage.getTokens()

        const accessMissingOrExpiring =
          !tokens.access || isTokenExpiring(tokens.access)
        const hasRefreshToken = Boolean(
          tokens.refresh && !isTokenExpired(tokens.refresh),
        )

        return accessMissingOrExpiring && hasRefreshToken
      }

      // Get token expiration information
      function getTokenExpiration(tokenType: "access" | "refresh") {
        return secureStorage.getTokenExpiration(tokenType)
      }

      // Set user profile
      function setProfile(user: TUser) {
        set((state) => {
          state.user = user
          // Don't automatically set isAuthenticated here - tokens should be set separately
        })
      }

      function setProfileName(name: string) {
        set((state) => {
          if (state.user) {
            state.user.name = name
          }
        })
      }

      // Remove user profile and clear auth
      function removeProfile() {
        clearAuth() // This will clear tokens and set isAuthenticated to false
      }

      // Permission checking
      function can(
        ...args: Parameters<ReturnType<typeof defineAbilityFor>["can"]>
      ) {
        const user = getUser()
        if (user) {
          return defineAbilityFor(user).can(...args)
        }
        return false // Default to false when no user
      }

      function cannot(
        ...args: Parameters<ReturnType<typeof defineAbilityFor>["cannot"]>
      ) {
        const user = getUser()
        if (user) {
          return defineAbilityFor(user).cannot(...args)
        }
        return true // Default to true when no user (cannot do anything)
      }

      return {
        // State
        isAuthenticated: false,
        isLoading: true,
        isNearExpiry: false,
        user: undefined,

        // Actions
        setTokens,
        clearAuth,
        setProfile,
        setProfileName,
        removeProfile,
        checkAuthStatus,
        getTokens,
        shouldRefresh,
        getTokenExpiration,
        can,
        cannot,

        // Internal
        initialize,
        startTokenMonitoring,
        stopTokenMonitoring,
      }
    }),
    {
      name: "auth",
      // Only persist user profile, not auth state (tokens are in secure storage)
      partialize: (state) => ({ user: state.user }),
    },
  ),
)

/**
 * Helper function to trigger authentication state updates across components
 * Useful when authentication changes happen outside of React state
 */
export function triggerAuthChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth-change"))
  }
}

/**
 * Hook for monitoring token expiration and automatic refresh
 * Uses the centralized auth store for better performance
 */
export function useTokenMonitor() {
  const { isNearExpiry, shouldRefresh } = useAuth()

  return {
    isNearExpiry,
    shouldRefresh,
  }
}
